import React, { useEffect, useState } from "react";
import { PublicityBannerConfig } from "../../helpers/publicity.banner.helper";
import "./publicity-banner.component.scss";

interface PublicityBannerProps {
  banner?: PublicityBannerConfig | null;
  className?: string;
}

const PublicityBanner = ({ banner, className = "" }: PublicityBannerProps) => {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [banner?.imageUrl]);

  if (!banner) {
    return null;
  }

  const hasImage = Boolean(banner.imageUrl && !imageFailed);
  const hasVisibleLink = Boolean(banner.linkUrl && banner.ctaLabel);
  const hasCopy = Boolean(banner.title || banner.subtitle || hasVisibleLink);
  const isImageOnlyLink = Boolean(hasImage && banner.linkUrl && !hasCopy);
  const bannerLabel = banner.ariaLabel || banner.title || "Sponsored banner";
  const bannerStyle = banner.background
    ? ({
        "--publicity-logo-ad-background": banner.background,
      } as React.CSSProperties)
    : undefined;
  const logoAdThemeClass = banner.theme
    ? `publicity-banner--logo-ad-${banner.theme}`
    : "";

  if (banner.logoUrl) {
    const logoBackground = hasImage ? (
      <>
        <img
          className="publicity-banner__background"
          src={banner.imageUrl}
          alt=""
          aria-hidden="true"
          onError={() => setImageFailed(true)}
        />
        <div
          className="publicity-banner__background-overlay"
          aria-hidden="true"
        />
      </>
    ) : null;
    const logoBannerContent = (
      <>
        <div className="publicity-banner__logo-frame">
          <img
            className="publicity-banner__logo"
            src={banner.logoUrl}
            alt={bannerLabel}
          />
        </div>
        <div className="publicity-banner__logo-content">
          {banner.title ? (
            <div className="publicity-banner__logo-title">{banner.title}</div>
          ) : null}
          {banner.subtitle ? (
            <div className="publicity-banner__logo-subtitle">
              {banner.subtitle}
            </div>
          ) : null}
          {banner.ctaLabel ? (
            <div className="publicity-banner__logo-link">{banner.ctaLabel}</div>
          ) : null}
        </div>
      </>
    );

    return (
      <aside
        className={`publicity-banner publicity-banner--logo-ad ${logoAdThemeClass} ${className}`}
        aria-label={bannerLabel}
        style={bannerStyle}
      >
        {logoBackground}
        {banner.linkUrl ? (
          <a
            className="publicity-banner__logo-ad-link"
            href={banner.linkUrl}
            target="_blank"
            rel="noreferrer"
          >
            {logoBannerContent}
          </a>
        ) : (
          <div className="publicity-banner__logo-ad-link">
            {logoBannerContent}
          </div>
        )}
      </aside>
    );
  }

  const content = (
    <>
      {hasImage ? (
        <img
          src={banner.imageUrl}
          alt={bannerLabel}
          onError={() => setImageFailed(true)}
        />
      ) : null}
      {hasImage && hasCopy ? (
        <div className="publicity-banner__scrim" aria-hidden="true" />
      ) : null}
      {hasCopy ? (
        <div className="publicity-banner__content">
          {banner.title ? (
            <div className="publicity-banner__title">{banner.title}</div>
          ) : null}
          {banner.subtitle ? (
            <div className="publicity-banner__subtitle">{banner.subtitle}</div>
          ) : null}
          {hasVisibleLink ? (
            <a
              className="publicity-banner__link"
              href={banner.linkUrl}
              target="_blank"
              rel="noreferrer"
            >
              {banner.ctaLabel || "Learn more"}
            </a>
          ) : null}
        </div>
      ) : null}
      {isImageOnlyLink ? (
        <a
          className="publicity-banner__image-link"
          href={banner.linkUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={bannerLabel}
        />
      ) : null}
    </>
  );

  return (
    <aside
      className={`publicity-banner ${
        hasImage ? "publicity-banner--image" : "publicity-banner--solid"
      } ${isImageOnlyLink ? "publicity-banner--image-only" : ""} ${className}`}
      aria-label={bannerLabel}
    >
      {content}
    </aside>
  );
};

export default PublicityBanner;
